import { Controller } from "@hotwired/stimulus"

// Controller for sortable lists that preserves search form values when sorting.
// Sort links trigger form submission with updated sort params, preserving any
// search filter values (even unsaved form changes).
//
// Usage:
//   <form id="my-search-form">
//     <input type="hidden" name="sort_by" id="my-search-form-sort-by">
//     <input type="hidden" name="sort_direction" id="my-search-form-sort-direction">
//   </form>
//   <table data-controller="sortable-list" data-sortable-list-form-id-value="my-search-form">
//     <th><a href="#" data-action="click->sortable-list#sort" data-field="created_at">Date</a></th>
//   </table>
export default class extends Controller {
  static values = {
    formId: String
  }

  connect() {
    // Listen for turbo frame load to hide loading indicator
    document.addEventListener("turbo:frame-load", this.hideLoading.bind(this))
  }

  disconnect() {
    document.removeEventListener("turbo:frame-load", this.hideLoading.bind(this))
  }

  sort(event) {
    event.preventDefault()

    const link = event.currentTarget
    const field = link.dataset.field

    if (!field) {
      console.warn("Sortable list: missing data-field attribute on sort link")
      return
    }

    // Find the search form by ID
    const form = document.getElementById(this.formIdValue)
    if (!form) {
      console.warn(`Sortable list: could not find form with id '${this.formIdValue}'`)
      return
    }

    // Find the hidden sort fields
    const sortByField = form.querySelector('input[name="sort_by"]')
    const sortDirectionField = form.querySelector('input[name="sort_direction"]')

    if (!sortByField || !sortDirectionField) {
      console.warn("Sortable list: missing sort_by or sort_direction hidden fields in form")
      return
    }

    // Determine new sort direction
    const currentSortBy = sortByField.value
    const currentDirection = sortDirectionField.value || "desc"

    let newDirection = "desc"
    if (currentSortBy === field) {
      // Toggle direction if clicking the same column
      newDirection = currentDirection === "asc" ? "desc" : "asc"
    }

    // Update hidden fields
    sortByField.value = field
    sortDirectionField.value = newDirection

    // Show loading indicator
    this.showLoading()

    // Submit the form
    form.requestSubmit()
  }

  showLoading() {
    // Add loading overlay to the table container
    const container = this.element.closest("[data-test-id$='-container']") || this.element.parentElement
    if (container && !container.querySelector(".sortable-loading-overlay")) {
      const overlay = document.createElement("div")
      overlay.className = "sortable-loading-overlay absolute inset-0 bg-white/70 flex items-center justify-center z-10"
      overlay.innerHTML = `
        <div class="flex items-center gap-2 text-gray-600">
          <svg class="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <span>Loading...</span>
        </div>
      `
      container.style.position = "relative"
      container.appendChild(overlay)
    }
  }

  hideLoading() {
    // Remove all loading overlays
    document.querySelectorAll(".sortable-loading-overlay").forEach(el => el.remove())
  }
}
